import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../components/Button';
import CreateEventModal from '../../components/vendor/CreateEventModal';
import { useUserProfile } from '../../hooks/useUserProfile';
import enhancedApiClient from '../../api/enhancedApiClient';

const VendorDashboard = () => {
  console.log('VendorDashboard component rendered');
  const navigate = useNavigate();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [events, setEvents] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [threads, setThreads] = useState([]);
  const [messages, setMessages] = useState({});
  const [vendor, setVendor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [vendorRevenue, setVendorRevenue] = useState(0);
  
  // Get current user from AWS Cognito
  const [currentUser, setCurrentUser] = useState(null);
  
  useEffect(() => {
    const getCurrentUser = async () => {
      try {
        const { Auth } = await import('aws-amplify');
        const user = await Auth.currentAuthenticatedUser();
        setCurrentUser(user);
      } catch (error) {
        console.log('No authenticated user');
      }
    };
    getCurrentUser();
  }, []);
  
  const { userProfile, loading: profileLoading } = useUserProfile(currentUser?.username);
  const vendorId = userProfile?.currentVendorId;
  
  console.log('VendorDashboard - userProfile:', userProfile);
  console.log('VendorDashboard - vendorId:', vendorId);
  console.log('VendorDashboard - loading:', loading);
  console.log('VendorDashboard - events:', events);
  console.log('VendorDashboard - vendor:', vendor);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      
      if (!vendorId) {
        setLoading(false);
        return;
      }
      
      try {
        const [eventsResponse, tasksResponse, vendorResponse] = await Promise.all([
          fetch(`https://etz8ecs10a.execute-api.ap-southeast-2.amazonaws.com/prod/api/vendors/${vendorId}/events`),
          fetch(`https://etz8ecs10a.execute-api.ap-southeast-2.amazonaws.com/prod/api/vendors/${vendorId}/tasks`),
          fetch(`https://etz8ecs10a.execute-api.ap-southeast-2.amazonaws.com/prod/api/vendors/${vendorId}`)
        ]);
        
        const eventsData = eventsResponse.ok ? await eventsResponse.json() : [];
        const tasksData = tasksResponse.ok ? await tasksResponse.json() : [];
        const vendorData = vendorResponse.ok ? await vendorResponse.json() : null;
        
        setEvents(eventsData);
        setTasks(tasksData);
        setVendor(vendorData);
        
        // Load threads and messages in parallel (optimized)
        const vendorTasks = tasksData.filter(t => t.createdByUserId === currentUser?.username);
        const threadPromises = eventsData.map(event => 
          enhancedApiClient.getThreads(event.id, currentUser?.username)
            .then(threadsData => threadsData.filter(t => 
              vendorTasks.some(task => task.id === t.taskId)
            ))
            .catch(error => {
              console.error('Error loading threads for event:', event.id, error);
              return [];
            })
        );
        
        const allThreadsArrays = await Promise.all(threadPromises);
        const allThreads = allThreadsArrays.flat();
        setThreads(allThreads);
        
        // Load only last message for each thread in parallel
        const messagePromises = allThreads.map(thread =>
          enhancedApiClient.getMessages(thread.threadId)
            .then(messagesData => ({ threadId: thread.threadId, messages: messagesData || [] }))
            .catch(() => ({ threadId: thread.threadId, messages: [] }))
        );
        
        const messagesResults = await Promise.all(messagePromises);
        const allMessages = {};
        messagesResults.forEach(result => {
          allMessages[result.threadId] = result.messages;
        });
        setMessages(allMessages);
        console.log('Vendor data:', vendorData);
        
        // Create event lookup map for tasks
        const eventMap = {};
        eventsData.forEach(event => {
          eventMap[event.id] = event.name;
        });
        
        // Add event names to tasks
        const tasksWithEvents = tasksData.map(task => {
          const eventId = task.PK?.replace('EVENT#', '');
          return {
            ...task,
            eventName: eventMap[eventId] || 'Unknown Event'
          };
        });
        setTasks(tasksWithEvents);

        // Load revenue from invoices across all events
        let totalRevenue = 0;
        for (const event of eventsData) {
          try {
            const invRes = await fetch(`https://etz8ecs10a.execute-api.ap-southeast-2.amazonaws.com/prod/api/invoices?eventId=${event.id}`);
            if (invRes.ok) {
              const invoices = await invRes.json();
              const vendorInvoices = invoices.filter(inv => inv.vendorId === vendorId);
              totalRevenue += vendorInvoices.reduce((sum, inv) => sum + (parseFloat(inv.total) || parseFloat(inv.depositAmount) || 0), 0);
            }
          } catch {}
        }
        setVendorRevenue(totalRevenue);
      } catch (error) {
        console.error('Error fetching vendor data:', error);
        console.log('VendorId:', vendorId);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [vendorId]);

  const activeEvents = events.filter(e => e.status === 'active').length;
  const completedEvents = events.filter(e => e.status === 'completed').length;
  const getStartOfWeek = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day;
    return new Date(d.setDate(diff));
  };
  
  const getEndOfWeek = (date) => {
    const startOfWeek = getStartOfWeek(date);
    return new Date(startOfWeek.getTime() + 6 * 24 * 60 * 60 * 1000);
  };
  
  const thisWeekStart = getStartOfWeek(new Date());
  const thisWeekEnd = getEndOfWeek(new Date());
  
  const eventsThisWeek = events.filter(e => {
    const eventDate = new Date(e.eventDate);
    return eventDate >= thisWeekStart && eventDate <= thisWeekEnd;
  }).length;

  const getDaysInMonth = (year, month) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (year, month) => {
    return new Date(year, month, 1).getDay();
  };

  const generateCalendarDays = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth();
    
    const daysInMonth = getDaysInMonth(year, month);
    const firstDay = getFirstDayOfMonth(year, month);
    
    const days = [];
    
    // Add empty cells for days before month starts
    for (let i = 0; i < firstDay; i++) {
      days.push(null);
    }
    
    // Add all days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(day);
    }
    
    return days;
  };

  const getEventsForDay = (day) => {
    if (!day) return [];
    
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth();
    const targetDate = new Date(year, month, day);
    targetDate.setHours(0, 0, 0, 0);
    
    return events.filter(event => {
      const eventDate = new Date(event.eventDate);
      eventDate.setHours(0, 0, 0, 0);
      return eventDate.getTime() === targetDate.getTime();
    });
  };

  const getTaskDueDateBadge = (dueDate) => {
    if (!dueDate) return null;
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const due = new Date(dueDate);
    due.setHours(0, 0, 0, 0);
    
    const diffTime = due - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) {
      return {
        text: `Overdue by ${Math.abs(diffDays)} day${Math.abs(diffDays) === 1 ? '' : 's'}`,
        emoji: '🔴',
        color: '#DC2626',
        bgColor: '#FEE2E2'
      };
    } else if (diffDays === 0) {
      return {
        text: 'Due today',
        emoji: '🟡',
        color: '#F59E0B',
        bgColor: '#FEF3C7'
      };
    } else if (diffDays === 1) {
      return {
        text: 'Due tomorrow',
        emoji: '🟡',
        color: '#F59E0B',
        bgColor: '#FEF3C7'
      };
    } else if (diffDays <= 7) {
      return {
        text: `Due in ${diffDays} days`,
        emoji: '🟡',
        color: '#F59E0B',
        bgColor: '#FEF3C7'
      };
    } else {
      return {
        text: `Due in ${diffDays} days`,
        emoji: '',
        color: '#6B7280',
        bgColor: 'transparent'
      };
    }
  };

  const getTimeGroup = (dueDate, eventDate) => {
    if (!dueDate || !eventDate) return null;
    
    const due = new Date(dueDate);
    const event = new Date(eventDate);
    const diffTime = event - due;
    const diffMonths = diffTime / (1000 * 60 * 60 * 24 * 30);
    
    if (diffMonths < 1) return 'final';
    if (diffMonths < 3) return '1to3';
    if (diffMonths < 6) return '3to6';
    return '6plus';
  };

  const getTaskResponsibilityStatus = (task) => {
    const isCreatedByVendor = task.createdByUserId === currentUser?.username;
    const isCompleted = task.status === 'completed';
    
    // Task created by client/external party
    if (!isCreatedByVendor) {
      if (isCompleted) {
        return {
          text: 'Completed - Awaiting Client Confirmation',
          color: '#F59E0B',
          bgColor: '#FEF3C7'
        };
      } else {
        return {
          text: 'Awaiting Your Action',
          color: '#DC2626',
          bgColor: '#FEE2E2'
        };
      }
    }
    
    // Task created by vendor
    if (isCreatedByVendor) {
      if (isCompleted) {
        return {
          text: 'Completed by Client',
          color: '#059669',
          bgColor: '#D1FAE5'
        };
      } else {
        return {
          text: 'Pending Client Action',
          color: '#6B7280',
          bgColor: '#F3F4F6'
        };
      }
    }
    
    return null;
  };

  if (!userProfile || loading || profileLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'center', 
        justifyContent: 'center',
        minHeight: '60vh',
        gap: '16px'
      }}>
        <div style={{
          width: '40px',
          height: '40px',
          border: '3px solid #E5E7EB',
          borderTop: '3px solid #6B9080',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}></div>
        <div style={{ color: '#6B7280', fontSize: '16px' }}>
          Loading dashboard...
        </div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="page-header" style={{
        backgroundColor: '#E8F4E8',
        padding: '48px 24px 200px 24px',
        textAlign: 'center',
        position: 'relative',
        marginBottom: '60px'
      }}>
        <h1 className="page-title" style={{ 
          fontSize: '56px', 
          fontWeight: '400', 
          color: '#1F2937',
          margin: 0,
          fontFamily: 'Georgia, serif',
          letterSpacing: '-0.02em'
        }}>
          {vendor?.name || 'Vendor Dashboard'}
        </h1>
        
        {/* Stats Cards */}
        <div className="stats-grid" style={{ 
          position: 'absolute',
          bottom: '60px',
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'grid', 
          gridTemplateColumns: 'repeat(3, 1fr)', 
          gap: '20px',
          width: '100%',
          maxWidth: '1200px',
          padding: '0 24px'
        }}>
          <div style={{ 
            backgroundColor: 'white',
            padding: '20px',
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            minWidth: '200px'
          }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#6B9080" strokeWidth="1.5">
              <path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
            </svg>
            <div>
              <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#1F2937', display: 'flex', alignItems: 'baseline', gap: '8px' }}>
                {activeEvents} <span style={{ fontSize: '16px', color: '#000000', fontWeight: 'normal' }}>active event{activeEvents !== 1 ? 's' : ''}</span>
              </div>
            </div>
          </div>

          <div style={{ 
            backgroundColor: 'white',
            padding: '20px',
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            minWidth: '200px'
          }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#6B9080" strokeWidth="1.5">
              <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
            <div>
              <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#1F2937', display: 'flex', alignItems: 'baseline', gap: '8px' }}>
                {completedEvents} <span style={{ fontSize: '16px', color: '#000000', fontWeight: 'normal' }}>completed event{completedEvents !== 1 ? 's' : ''}</span>
              </div>
            </div>
          </div>

          <div style={{ 
            backgroundColor: 'white',
            padding: '20px',
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            minWidth: '200px'
          }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#6B9080" strokeWidth="1.5">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
              <line x1="16" y1="2" x2="16" y2="6"/>
              <line x1="8" y1="2" x2="8" y2="6"/>
              <line x1="3" y1="10" x2="21" y2="10"/>
            </svg>
            <div>
              <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#1F2937', display: 'flex', alignItems: 'baseline', gap: '8px' }}>
                {eventsThisWeek} <span style={{ fontSize: '16px', color: '#000000', fontWeight: 'normal' }}>event{eventsThisWeek !== 1 ? 's' : ''} this week</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 24px 24px 24px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '24px' }}>
          
          {/* Upcoming Checklist Tasks */}
          <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <h3 style={{ fontSize: '20px', fontWeight: '600', color: '#1F2937', marginBottom: '20px', fontFamily: 'Georgia, serif' }}>
              Upcoming Checklist Tasks
            </h3>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {(() => {
                // Filter incomplete tasks
                const incompleteTasks = tasks.filter(task => task.status !== 'completed');
                
                // Sort by due date
                const sortedTasks = incompleteTasks.sort((a, b) => {
                  if (!a.dueDate) return 1;
                  if (!b.dueDate) return -1;
                  return new Date(a.dueDate) - new Date(b.dueDate);
                });
                
                if (sortedTasks.length === 0) {
                  return (
                    <div style={{ textAlign: 'center', color: '#6B7280', padding: '20px' }}>
                      No upcoming tasks
                    </div>
                  );
                }
                
                // Group tasks by time period
                const groupedTasks = {
                  'overdue': [],
                  'final': [],
                  '1to3': [],
                  '3to6': [],
                  '6plus': []
                };
                
                sortedTasks.forEach(task => {
                  // Check if overdue
                  if (task.dueDate) {
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    const due = new Date(task.dueDate);
                    due.setHours(0, 0, 0, 0);
                    if (due < today) {
                      groupedTasks['overdue'].push(task);
                      return;
                    }
                  }
                  
                  // Get event date from first event (assuming vendor works on one event at a time)
                  const eventId = task.PK?.replace('EVENT#', '');
                  const taskEvent = events.find(e => e.id === eventId);
                  const timeGroup = getTimeGroup(task.dueDate, taskEvent?.eventDate);
                  if (timeGroup && groupedTasks[timeGroup]) {
                    groupedTasks[timeGroup].push(task);
                  }
                });
                
                // Display tasks from each group (max 6 total)
                const displayTasks = [];
                const groupOrder = ['overdue', 'final', '1to3', '3to6', '6plus'];
                const groupLabels = {
                  'overdue': 'Overdue',
                  'final': 'Final Month',
                  '1to3': '1-3 Months',
                  '3to6': '3-6 Months',
                  '6plus': '6+ Months'
                };
                
                for (const group of groupOrder) {
                  if (displayTasks.length >= 6) break;
                  const remaining = 6 - displayTasks.length;
                  const tasksToAdd = groupedTasks[group].slice(0, remaining);
                  tasksToAdd.forEach(task => {
                    displayTasks.push({ ...task, timeLabel: groupLabels[group] });
                  });
                }
                
                return displayTasks.map((task, index) => {
                  const dueDateBadge = getTaskDueDateBadge(task.dueDate);
                  const responsibilityStatus = getTaskResponsibilityStatus(task);
                  
                  return (
                    <div 
                      key={task.id} 
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '12px',
                        padding: '16px',
                        backgroundColor: dueDateBadge?.color === '#DC2626' ? '#FEF2F2' : '#f8fafc',
                        borderRadius: '8px',
                        border: dueDateBadge?.color === '#DC2626' ? '1px solid #FCA5A5' : 'none',
                        cursor: 'pointer'
                      }}
                      onClick={() => navigate('/vendor/checklist')}
                    >
                      <div style={{
                        width: '32px',
                        height: '32px',
                        backgroundColor: task.status === 'completed' ? '#6B9080' : '#E5E7EB',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: task.status === 'completed' ? 'white' : '#6B7280',
                        fontSize: '16px',
                        fontWeight: '600',
                        flexShrink: 0,
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }} 
                      onClick={async (e) => {
                        e.stopPropagation();
                        try {
                          const eventId = task.PK?.replace('EVENT#', '');
                          const newStatus = task.status === 'completed' ? 'pending' : 'completed';
                          await enhancedApiClient.updateTask(eventId, task.id, { status: newStatus });
                          // Refresh tasks
                          const response = await fetch(`https://etz8ecs10a.execute-api.ap-southeast-2.amazonaws.com/prod/api/vendors/${vendorId}/tasks`);
                          const tasksData = response.ok ? await response.json() : [];
                          const eventMap = {};
                          events.forEach(event => {
                            eventMap[event.id] = event.name;
                          });
                          const tasksWithEvents = tasksData.map(task => {
                            const eventId = task.PK?.replace('EVENT#', '');
                            return {
                              ...task,
                              eventName: eventMap[eventId] || 'Unknown Event'
                            };
                          });
                          setTasks(tasksWithEvents);
                        } catch (error) {
                          alert('❌ Failed to update task.');
                        }
                      }}
                      onMouseOver={(e) => {
                        if (task.status !== 'completed') {
                          e.currentTarget.style.backgroundColor = '#6B9080';
                          e.currentTarget.style.color = 'white';
                        }
                      }}
                      onMouseOut={(e) => {
                        if (task.status !== 'completed') {
                          e.currentTarget.style.backgroundColor = '#E5E7EB';
                          e.currentTarget.style.color = '#6B7280';
                        }
                      }}>
                        {task.status === 'completed' ? '✓' : ''}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ 
                          fontWeight: '500', 
                          fontSize: '14px',
                          marginBottom: '4px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {task.name}
                        </div>
                        <div style={{ 
                          fontSize: '11px', 
                          color: '#6B7280',
                          marginBottom: '6px'
                        }}>
                          {task.timeLabel} • {task.eventName}
                        </div>
                        {responsibilityStatus && (
                          <div style={{
                            display: 'inline-block',
                            fontSize: '11px',
                            fontWeight: '500',
                            color: responsibilityStatus.color,
                            backgroundColor: responsibilityStatus.bgColor,
                            padding: '3px 8px',
                            borderRadius: '10px',
                            marginBottom: '4px'
                          }}>
                            {responsibilityStatus.text}
                          </div>
                        )}
                        {dueDateBadge && (
                          <div style={{
                            display: 'block',
                            fontSize: '11px',
                            fontWeight: '500',
                            color: dueDateBadge.color,
                            marginTop: '4px'
                          }}>
                            {dueDateBadge.text}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                });
              })()}
            </div>
            
            <div style={{ marginTop: '20px', textAlign: 'center' }}>
              <button 
                onClick={() => navigate('/vendor/checklist')}
                style={{
                color: '#6B9080',
                backgroundColor: 'transparent',
                border: 'none',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                margin: '0 auto'
              }}>
                View full checklist
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M5 12h14M12 5l7 7-7 7"/>
                </svg>
              </button>
            </div>
          </div>

          {/* New Messages */}
          <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <h3 style={{ fontSize: '20px', fontWeight: '600', color: '#1F2937', marginBottom: '20px', fontFamily: 'Georgia, serif' }}>
              New Messages
            </h3>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {threads.length === 0 ? (
                <div style={{ textAlign: 'center', color: '#6B7280', padding: '20px' }}>
                  No new threads
                </div>
              ) : (() => {
                // Filter threads waiting for vendor response (last message not from vendor)
                const threadsWaitingResponse = threads.filter(thread => {
                  const threadMessages = messages[thread.threadId] || [];
                  const lastMessage = threadMessages[threadMessages.length - 1];
                  return lastMessage && lastMessage.sender !== currentUser?.username;
                });
                
                // Sort by latest message timestamp
                const sortedThreads = threadsWaitingResponse.sort((a, b) => {
                  const aMessages = messages[a.threadId] || [];
                  const bMessages = messages[b.threadId] || [];
                  const aLastMsg = aMessages[aMessages.length - 1];
                  const bLastMsg = bMessages[bMessages.length - 1];
                  const aTime = aLastMsg?.timestamp || a.createdAt;
                  const bTime = bLastMsg?.timestamp || b.createdAt;
                  return new Date(bTime) - new Date(aTime);
                });
                
                if (sortedThreads.length === 0) {
                  return (
                    <div style={{ textAlign: 'center', color: '#6B7280', padding: '20px' }}>
                      No threads waiting for response
                    </div>
                  );
                }
                
                return sortedThreads.slice(0, 3).map(thread => {
                  const task = tasks.find(t => t.id === thread.taskId);
                  return (
                    <div key={thread.threadId} style={{
                      padding: '12px',
                      backgroundColor: '#F9FAFB',
                      borderRadius: '8px',
                      cursor: 'pointer'
                    }} onClick={() => navigate(`/vendor/messages?threadId=${thread.threadId}`)}>
                      <div style={{ fontWeight: '500', color: '#1F2937', fontSize: '14px', marginBottom: '4px' }}>
                        {task?.name || 'Task'}
                      </div>
                      <div style={{ color: '#6B7280', fontSize: '12px' }}>
                        {new Date(thread.createdAt).toLocaleDateString()}
                      </div>
                    </div>
                  );
                });
              })()}
            </div>
            
            <div style={{ marginTop: '20px', textAlign: 'center' }}>
              <button 
                onClick={() => navigate('/vendor/messages')}
                style={{
                color: '#6B9080',
                backgroundColor: 'transparent',
                border: 'none',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                margin: '0 auto'
              }}>
                View all threads
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M5 12h14M12 5l7 7-7 7"/>
                </svg>
              </button>
            </div>
          </div>

          {/* Future Revenue */}
          <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <h3 style={{ fontSize: '20px', fontWeight: '600', color: '#1F2937', marginBottom: '8px', fontFamily: 'Georgia, serif' }}>
              Revenue Snapshot
            </h3>
            <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#1F2937', marginBottom: '20px' }}>
              ${vendorRevenue.toLocaleString()}
            </div>
            
            {/* Simple Chart Representation */}
            <div style={{ display: 'flex', alignItems: 'end', gap: '8px', height: '100px', marginBottom: '16px' }}>
              {(() => {
                const currentMonth = new Date().getMonth();
                const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                const displayMonths = [];
                for (let i = 0; i < 6; i++) {
                  displayMonths.push(months[(currentMonth + i) % 12]);
                }
                return displayMonths.map((month, index) => {
                  const monthRevenue = events.filter(event => {
                    const eventMonth = new Date(event.eventDate).getMonth();
                    const targetMonth = (currentMonth + index) % 12;
                    const isConfirmed = event.status === 'confirmed' || event.status === 'active';
                    return eventMonth === targetMonth && isConfirmed;
                  }).reduce((sum, event) => sum + (parseInt(event.budget) || 0), 0);
                  
                  const maxRevenue = Math.max(...displayMonths.map((_, i) => 
                    events.filter(event => {
                      const eventMonth = new Date(event.eventDate).getMonth();
                      const targetMonth = (currentMonth + i) % 12;
                      const isConfirmed = event.status === 'confirmed' || event.status === 'active';
                      return eventMonth === targetMonth && isConfirmed;
                    }).reduce((sum, event) => sum + (parseInt(event.budget) || 0), 0)
                  ));
                  
                  const height = maxRevenue > 0 ? Math.max(20, (monthRevenue / maxRevenue) * 80) : 20;
                  
                  return (
                    <div key={month} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <div style={{
                        width: '100%',
                        backgroundColor: index === 0 ? '#6B9080' : '#E5E7EB',
                        height: `${height}px`,
                        borderRadius: '4px 4px 0 0',
                        marginBottom: '8px'
                      }}></div>
                      <div style={{ fontSize: '12px', color: '#6B7280' }}>{month}</div>
                    </div>
                  );
                });
              })()}
            </div>
            
            <div style={{ textAlign: 'center' }}>
              <button 
                onClick={() => navigate('/vendor/payments')}
                style={{
                color: '#6B9080',
                backgroundColor: 'transparent',
                border: 'none',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                margin: '0 auto'
              }}>
                View insights
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M5 12h14M12 5l7 7-7 7"/>
                </svg>
              </button>
            </div>
            
            <div style={{ marginTop: '24px' }}>
              <button 
                onClick={() => setShowCreateModal(true)}
                style={{
                  width: '100%',
                  padding: '12px 24px',
                  backgroundColor: '#6B9080',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                Create New Event
              </button>
            </div>
          </div>
        </div>

        {/* Calendar View */}
        <div style={{ marginTop: '32px' }}>
          <h3 style={{ fontSize: '24px', fontWeight: '600', color: '#1F2937', marginBottom: '20px', fontFamily: 'Georgia, serif' }}>
            {new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
          </h3>
          
          <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            {/* Calendar Header - Days of Week */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '8px', marginBottom: '12px' }}>
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                <div key={day} style={{ 
                  textAlign: 'center', 
                  fontWeight: '600', 
                  fontSize: '14px', 
                  color: '#6B7280',
                  padding: '8px 0'
                }}>
                  {day}
                </div>
              ))}
            </div>
            
            {/* Calendar Grid */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '8px' }}>
              {generateCalendarDays().map((day, index) => {
                const dayEvents = getEventsForDay(day);
                const today = new Date();
                const isToday = day === today.getDate();
                
                return (
                  <div
                    key={index}
                    style={{
                      minHeight: '100px',
                      padding: '8px',
                      backgroundColor: day ? (isToday ? '#E8F4E8' : '#F9FAFB') : 'transparent',
                      borderRadius: '8px',
                      border: isToday ? '2px solid #6B9080' : '1px solid #E5E7EB',
                      position: 'relative'
                    }}
                  >
                    {day && (
                      <>
                        <div style={{ 
                          fontSize: '14px', 
                          fontWeight: isToday ? '700' : '500',
                          color: isToday ? '#6B9080' : '#1F2937',
                          marginBottom: '4px'
                        }}>
                          {day}
                        </div>
                        
                        {dayEvents.length > 0 && (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            {dayEvents.slice(0, 2).map((event, idx) => (
                              <div
                                key={idx}
                                style={{
                                  fontSize: '11px',
                                  padding: '4px 6px',
                                  backgroundColor: '#6B9080',
                                  color: 'white',
                                  borderRadius: '4px',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  cursor: 'pointer'
                                }}
                                onClick={() => navigate('/vendor/events', { state: { selectedEventId: event.id } })}
                                title={event.name}
                              >
                                {event.name}
                              </div>
                            ))}
                            {dayEvents.length > 2 && (
                              <div style={{
                                fontSize: '10px',
                                color: '#6B7280',
                                fontWeight: '500',
                                marginTop: '2px'
                              }}>
                                +{dayEvents.length - 2} more
                              </div>
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
      
      <CreateEventModal 
        isOpen={showCreateModal} 
        onClose={() => setShowCreateModal(false)} 
      />
    </div>
  );
};

export default VendorDashboard;